import { element, elements } from '@shared/easyfy.core';
import { IFacetGroupCount, IFacetValueCount, ISorter, IUpdatePageParams } from '@clientShared/filter.types';
import { hide, show } from '@shared/easyfy.utils';

export class FilterDOMHelper {
  static getButtonElement(target: HTMLElement): HTMLElement {
    return target.hasAttribute('data-page') ? target : (target.parentElement as HTMLElement);
  }

  static getFilterHeaderContainer(): HTMLElement {
    return element('.filter-header-container') as HTMLElement;
  }

  static getFilterHeaderButton(): HTMLElement {
    return element('.sticky-filter-header') as HTMLElement;
  }

  static getProductListName(itemsQuerySelector: string): string {
    return element(itemsQuerySelector)?.dataset['listName'];
  }

  static getProductListId(itemsQuerySelector: string): string {
    return element(itemsQuerySelector)?.dataset['listId'];
  }

  static getPage(button: HTMLElement): number {
    return button.dataset['page'] != null ? Number.parseInt(button.dataset['page']) : 1;
  }

  static getPageId(): string {
    let pageId = element('[data-container="page"]')?.dataset['pageId'] ?? element('[data-container="paging-container"]')?.dataset['pageId'];
    return pageId?.toString();
  }

  static getTotalPages(button: HTMLElement): number {
    const buttonParent = button.closest('[data-page-id]') as HTMLElement;
    return +buttonParent.dataset['totalPages'];
  }

  static clearSelectedFilters(): void {
    element('[data-container="all-selected-filters"]')
      ?.querySelectorAll('[data-container="selected-filter"]')
      ?.forEach((el) => {
        el?.remove();
      });
  }

  static uncheckAllFilters(filterQuerySelector: string): void {
    const filterCheckboxes: NodeListOf<HTMLInputElement> = FilterDOMHelper.getFilterCheckboxes(filterQuerySelector);
    if (!filterCheckboxes || filterCheckboxes.length < 1) return;

    filterCheckboxes.forEach((checkbox: HTMLInputElement) => {
      checkbox.checked = false;
    });
  }

  static updatedPageOnFilterChange(params: IUpdatePageParams): void {
    const responseData = params.responseData;
    const filterContainer = element(params.filterQuerySelector);
    const pagingContainer = element(params.pagingQuerySelector);
    const itemsContainer = element(params.itemsQuerySelector);
    const sorterContainers = elements(params.sortersQuerySelector);
    const selectedFiltersContainer = element(params.selectedFiltersQuerySelector);

    if (params.replaceProducts && itemsContainer) {
      itemsContainer.innerHTML = responseData.itemsPartial;
    } else {
      itemsContainer?.children?.length > 0
        ? itemsContainer.lastElementChild.insertAdjacentHTML('afterend', responseData.itemsPartial)
        : (itemsContainer.innerHTML = responseData.itemsPartial);
    }

    if (filterContainer) {
      filterContainer.innerHTML = responseData.filterPartial;
    }

    if (pagingContainer) {
      pagingContainer.innerHTML = responseData.pagingPartial;
    }

    if (selectedFiltersContainer) {
      selectedFiltersContainer.innerHTML = responseData.selectedFiltersPartial;
    }

    if (sorterContainers) {
      sorterContainers.forEach((sorterContainer) => {
        sorterContainer.innerHTML = responseData.sortPartial;
      });
    }
  }

  static getSortData(target: HTMLElement): ISorter {
    const selectedValue = target?.dataset['value'];
    const key = selectedValue?.split('-')[0];
    const sortDirection = selectedValue?.split('-')[1];

    return {
      key,
      sortDirection,
    };
  }

  static getPageFromSearchFragment(): number {
    const url = new URLSearchParams(window.location.search);
    const page = Number.parseInt(url.get('p'));
    return !Number.isNaN(page) ? page : 1;
  }

  static getFacetCountsForCurrentGroup(input: HTMLElement): IFacetGroupCount | null {
    const filterGroupElement = input.closest('[data-container="filter-group"]') as HTMLElement;
    const facetGroupKey = filterGroupElement?.id;
    const filterGroup = element('.filter-groups #' + facetGroupKey) as HTMLElement;
    if (!filterGroup) return null;

    const countData: Array<IFacetValueCount> = [];

    filterGroup.querySelectorAll('.filter-row [data-action="change-filter"]')?.forEach((filterInput) => {
      countData.push({
        value: filterInput.attributes['value']?.value,
        count: Number.parseInt(filterInput.attributes['data-result']?.value) ?? 0,
      });
    });

    const facetGroupCountValues: IFacetGroupCount = {
      key: facetGroupKey,
      valueCountData: countData,
    };

    return facetGroupCountValues;
  }

  static closeFilterOptionSelector(target: HTMLElement): void {
    const details: NodeListOf<HTMLElement> = document.querySelectorAll('.filter-item');

    details.forEach((el) => {
      if (!el.contains(target) && el !== target) {
        el.removeAttribute('open');
      }
    });
  }

  static toggleClearFiltersButton(anyFilterChecked: boolean): void {
    const clearFilterLinks = document.querySelectorAll('[data-action="clear-filters"]');
    if (clearFilterLinks) {
      clearFilterLinks.forEach((clearFilterLink) => {
        anyFilterChecked ? show(clearFilterLink, 'flex') : hide(clearFilterLink);
      });
    }
  }

  static updateFilterCount(): void {
    const filterCounter = document.querySelector('[data-container="filter-counter"]');
    const numberOfFilters = document.querySelectorAll('input[data-action="change-filter"]:checked')?.length;

    if (filterCounter) {
      filterCounter.innerHTML = numberOfFilters > 0 ? `(${numberOfFilters})` : '';
    }
  }

  //#region Private Methods
  private static getFilterContainer(filterQuerySelector: string): HTMLElement {
    return element(filterQuerySelector) as HTMLElement;
  }

  private static getFilterCheckboxes(filterQuerySelector: string): NodeListOf<HTMLInputElement> {
    return this.getFilterContainer(filterQuerySelector)?.querySelectorAll('input:checked');
  }
  //#endregion
}
